import React, { useEffect } from 'react';
import './ui.css';

/**
 * Lightweight shared toast/banner primitive (Tasks Rebuild Task 12).
 *
 * Reconnects success/failure feedback that got lost when CreateTaskModal was
 * extracted from Tasks.tsx in Task 8 (that extraction dropped the page-level
 * `message` state the legacy page used to show "Task created successfully"
 * -- see task-8-report.md's review flag) and gives the lane bodies + the new
 * TaskDetailPane/bulk actions a single place to surface one-line feedback
 * ("Assigned to claim CL123", "3/4 completed -- 1 failed", etc.) without each
 * lane reinventing its own banner markup.
 *
 * Deliberately NOT a context/provider -- the lane pages already pass a lot of
 * state down through LaneShell's render-prop slots (LaneListSlotProps/
 * LaneDetailSlotProps), so a `showToast` callback threaded the same way (see
 * LaneShell.tsx) is simpler than adding a second state-sharing mechanism.
 */

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  type: ToastType;
  message: string;
  /** Optional one-click follow-up ("Show it", "Try again"). */
  action?: { label: string; onClick: () => void };
}

export interface ToastProps {
  toast: ToastMessage | null;
  onDismiss: () => void;
  /** Auto-dismiss delay in ms; 0 disables auto-dismiss. */
  autoDismissMs?: number;
}

export const Toast: React.FC<ToastProps> = ({ toast, onDismiss, autoDismissMs = 4000 }) => {
  useEffect(() => {
    if (!toast || autoDismissMs <= 0) return;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, autoDismissMs]);

  if (!toast) return null;

  return (
    <div className={`ui-toast ui-toast--${toast.type}`} role="status">
      <span className="ui-toast__message">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className="ui-toast__action"
          onClick={() => {
            const action = toast.action;
            onDismiss();
            action?.onClick();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="ui-toast__dismiss" aria-label="Dismiss notification" onClick={onDismiss}>
        &times;
      </button>
    </div>
  );
};

export default Toast;
